import { Button, Conditional } from '@payaca/ambrosia';
import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { scrollBehaviour } from '@payaca/utilities/motionUtilities';
import {
  createContext,
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  NavLink,
  Route,
  Switch,
  useHistory,
  useRouteMatch,
} from 'react-router-dom';

export type TabPanel = {
  icon?: TIconName;
  label: string;
  slug: string;
  render: () => ReactNode;
};

type Variant = 'default' | 'segment' | 'pilled';

// HiDPI displays can truncate max scrollLeft ~1px short of the end.
const SCROLL_EDGE_TOLERANCE_PX = 1;

export interface RouterTabsProps {
  panels: TabPanel[];
}

const RouterTabsContext = createContext<RouterTabsProps>({ panels: [] });

const RouterTabs: FC<PropsWithChildren<RouterTabsProps>> = (props) => {
  const { panels, children } = props;

  return (
    <RouterTabsContext.Provider value={{ panels }}>
      {children}
    </RouterTabsContext.Provider>
  );
};

const RouterTabNav: FC<{
  defaultTabSlug?: string;
  className?: string;
  fullWidth?: boolean;
  navSuffix?: ReactNode;
  variant?: Variant;
  onChange?: (tabSlug: string) => void;
}> = (props) => {
  const {
    className,
    defaultTabSlug,
    navSuffix,
    onChange,
    fullWidth = false,
    variant = 'default',
  } = props;

  const { panels } = useContext(RouterTabsContext);

  const history = useHistory();
  const { path, url } = useRouteMatch();
  const hasActiveTab = useRouteMatch<{
    tabSlug: string;
  }>(`${path}/:tabSlug`);
  const queryString = new URLSearchParams(window.location.search).toString();

  const {
    ref: navRef,
    isOverflowing,
    canScrollLeft,
    canScrollRight,
    refresh,
    scrollToStart,
    scrollToEnd,
  } = useScrollOverflow<HTMLElement>();

  const activeTabIsValid =
    Boolean(hasActiveTab) &&
    panels.some((panel) => panel.slug === hasActiveTab?.params.tabSlug);

  useEffect(() => {
    if (panels.length === 0) return;
    if (defaultTabSlug && !activeTabIsValid) {
      history.replace(
        `${url}/${defaultTabSlug}${queryString ? `?${queryString}` : ''}`
      );
    }
  }, [
    history,
    activeTabIsValid,
    url,
    queryString,
    defaultTabSlug,
    panels.length,
  ]);

  useEffect(() => {
    if (hasActiveTab?.params?.tabSlug) {
      onChange?.(hasActiveTab.params.tabSlug);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'onChange', and 'hasActiveTab.params.tabSlug'
  }, [hasActiveTab?.url]);

  // Changing panels shifts scrollWidth without firing a scroll/resize event.
  useEffect(() => {
    refresh();
  }, [refresh, panels.length]);

  // Centre the active tab. scrollTo (not scrollIntoView) avoids vertical nudging.
  useEffect(() => {
    const nav = navRef.current;
    const active = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !active) return;
    const activeLeftWithinNav =
      active.getBoundingClientRect().left -
      nav.getBoundingClientRect().left +
      nav.scrollLeft;
    const target =
      activeLeftWithinNav - nav.clientWidth / 2 + active.offsetWidth / 2;
    nav.scrollTo({ left: target, behavior: scrollBehaviour() });
  }, [navRef, hasActiveTab?.params?.tabSlug, panels.length]);

  return (
    <div
      className={clstx(
        'grid justify-between',
        navSuffix && 'gap-x-4',
        className
      )}
      style={{
        gridTemplateColumns: navSuffix ? '1fr auto' : '1fr',
      }}
    >
      <div className="relative min-w-0">
        <nav
          ref={navRef}
          className={clstx(
            'w-full items-end gap-x-4 text-base',
            isOverflowing ? 'overflow-x-scroll' : 'overflow-x-hidden',
            (variant === 'default' || variant === 'pilled') && 'tabs',
            variant === 'segment' && 'tabs--segment'
          )}
          aria-label="Tabs"
          role="tablist"
        >
          {panels.map((tab) => {
            // NavLink flags the active tab with `aria-current="page"`; mirror
            // that onto `aria-selected` so the tablist satisfies the WAI-ARIA
            // tab pattern. `hasActiveTab` resolves the current slug from the
            // same route match that drives `aria-current`, so the two agree.
            const isActive = hasActiveTab?.params?.tabSlug === tab.slug;

            return (
              <NavLink
                key={tab.slug}
                className={clstx(
                  'tab gap-x-3 font-medium text-blue-900 hover:no-underline',
                  variant === 'default' && 'tab--underline',
                  variant === 'segment' && 'tab--segment',
                  variant === 'pilled' && 'tab--pilled',
                  fullWidth && 'flex-1'
                )}
                to={`${url}/${tab.slug}`}
                role="tab"
                aria-selected={isActive}
              >
                <Conditional condition={!!tab.icon}>
                  <UntitledIcon name={tab.icon!} className="h-5 w-5" />
                </Conditional>

                {tab.label}
              </NavLink>
            );
          })}
        </nav>
        <ScrollChevron
          direction="left"
          visible={canScrollLeft}
          onClick={scrollToStart}
        />
        <ScrollChevron
          direction="right"
          visible={canScrollRight}
          onClick={scrollToEnd}
        />
      </div>
      {navSuffix && navSuffix}
    </div>
  );
};

function useScrollOverflow<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScrollLeft = scrollWidth - clientWidth;
    setIsOverflowing(maxScrollLeft > SCROLL_EDGE_TOLERANCE_PX);
    setCanScrollLeft(scrollLeft > SCROLL_EDGE_TOLERANCE_PX);
    setCanScrollRight(maxScrollLeft - scrollLeft > SCROLL_EDGE_TOLERANCE_PX);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [update]);

  const scrollToStart = useCallback(() => {
    ref.current?.scrollTo({ left: 0, behavior: scrollBehaviour() });
  }, []);

  const scrollToEnd = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTo({ left: el.scrollWidth, behavior: scrollBehaviour() });
  }, []);

  return {
    ref,
    isOverflowing,
    canScrollLeft,
    canScrollRight,
    refresh: update,
    scrollToStart,
    scrollToEnd,
  };
}

// Floats over a scroll edge with a gradient mask; hidden once that edge is reached.
const ScrollChevron: FC<{
  direction: 'left' | 'right';
  visible: boolean;
  onClick: () => void;
}> = ({ direction, visible, onClick }) => {
  if (!visible) return null;
  return (
    <div
      className={clstx(
        // Click-through so the strip never blocks the edge tab beneath it.
        'pointer-events-none absolute inset-y-0 z-10 flex items-center from-white via-white',
        direction === 'left'
          ? 'left-0 bg-linear-to-r pr-6'
          : 'right-0 bg-linear-to-l pl-6'
      )}
    >
      <Button
        type="button"
        aria-label={
          direction === 'left' ? 'Scroll tabs left' : 'Scroll tabs right'
        }
        onClick={onClick}
        variant="quaternary"
        className="pointer-events-auto shrink-0"
      >
        <UntitledIcon
          name={direction === 'right' ? 'chevron-right' : 'chevron-left'}
          className="h-4 w-4"
        />
      </Button>
    </div>
  );
};

const RouterTabPanels: FC = () => {
  const { panels } = useContext(RouterTabsContext);

  const { path } = useRouteMatch();

  return (
    <Switch>
      {panels.map((tab) => {
        return (
          <Route key={tab.slug} path={`${path}/${tab.slug}`}>
            {tab.render()}
          </Route>
        );
      })}
    </Switch>
  );
};

export default Object.assign(RouterTabs, {
  Nav: RouterTabNav,
  Panels: RouterTabPanels,
});
